import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CompareTool.module.scss';

//FlexGrid
import { CollectionView } from '@mescius/wijmo';
import { FlexGrid as PureFlexGrid } from '@mescius/wijmo.grid';
import { FlexGridDetailProvider } from '@mescius/wijmo.grid.detail';
import { FlexGridFilter as PureFlexGridFilter } from '@mescius/wijmo.grid.filter';
import * as wjGridXlsx from '@mescius/wijmo.grid.xlsx';
import '@mescius/wijmo.styles/wijmo.css';

import { WjRef } from '@mescius/wijmo.react.base';
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn, ICellTemplateContext } from '@mescius/wijmo.react.grid';
import { FlexGridDetail } from '@mescius/wijmo.react.grid.detail';
import { FlexGridFilter } from '@mescius/wijmo.react.grid.filter';
import { CollectionViewNavigator } from '@mescius/wijmo.react.input';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from '@mui/material/Link';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { CompareDisplayMode, ComparisonViewModel, Part } from '../../models/Api';
import { convertDecimalStringToNumber, launchConfigureTool } from '../../utils/Utils';
import SizingToolService, { compareCleanupEventType } from '../../services/SizingToolService';

//interfaces
export interface ICompareResults {
  compareResultsContainer: ICompareResultsContainer;
}

export interface ICompareResultsContainer {
  compareViewModel: ICompareViewModel;
}

export interface ICompareViewModel {
  wfid: string;
  attributeHeaders: ICompareAttributeHeader[];
  compareTitle: string;
  products: ICompareProduct[];
  neverFilterColumns: string[];
}

export interface ICompareAttributeHeader {
  columnNumber: number;
  displayOnCompareScreen: string;
  displayType: string;
  longName: string;
  maxWidth: number;
  minWidth: number;
  originalKeyName: string;
  selectedUnit: number;
  shortName: string;
  units: string;
}

export interface ICompareProduct {
  attributes: ICompareProductAttribute[];
  configUrl: string;
  description: string;
  group: number;
  imageUrl: string;
  index: number;
  isBest: boolean;
  orderNumber: number;
  partNumber: string;
  productName: string;
  productUrl: string;
  seriesId: number;
  seriesName: string;
  title: string;
  validity: string;
  zoomImgUrl: string;
}

export interface ICompareProductAttribute {
  valueNumber: number;
  valueString: string;
}

enum GridType {
  Main,
  Nested,
}

export default function CompareTool(props: {
  workflowId: string;
  partCategoryName: string;
  productAttributeKeyForGrouping: string;
  locale: string;
  closeCompare: () => void;
  viewModel: ComparisonViewModel;
  part?: Part;
}) {
  //prep data for binding
  interface IToolHeaderData {
    key: string;
    modelIndex: number;
    align: string;
    bindingValue: string;
    headerText: string;
    valueFormat: string;
    maxWidth: number;
    minWidth: number;
    visible: boolean;
    displayMode: CompareDisplayMode;
  }

  interface OptimizationLogData {
    Header: string;
    Lines: string[];
  }

  interface IToolProductAttribute {
    valueNumber: number;
    valueString: string;
    displayNumber: number;
  }

  interface IToolChildItem {
    values: (number | string)[];
  }
  interface IToolItem {
    key: string;
    attributes: IToolProductAttribute[];
    seriesName: string;
    productName: string;
    configUrl: string;
    children: IToolChildItem[];
  }

  interface IToolGridData {
    title: string;
    headers: IToolHeaderData[];
    childHeaders: IToolHeaderData[];
    items: IToolItem[];
    filterColumns: string[];
  }

  interface ICompareChildColumn {
    binding: string;
    header: string;
    allowSorting: boolean;
  }

  interface ICompareChildData {
    itemsSource: Record<string, number | string>[];
    columns: ICompareChildColumn[];
  }

  const { t } = useTranslation();
  let groupByBinding = 'productName'; //initialize.  may be changed based on props
  const productSeriesColumnKey = 'ProductSeries'; // PDN calls it Product but the other divisions call it Series
  const configureColumnKey = 'Configure';
  const reportColumnKey = 'Rpt';
  const optimizationLogColumnKey = 'OptimizationLogJson';
  const childColumnKey = 'ChildRowCompareDataJson';

  const innerFlexGridMinWidthMap = new Map();
  const updateInnerFlexGridMinWidthMap = (childColData: ICompareChildColumn[]) => {
    childColData.forEach((col) => {
      innerFlexGridMinWidthMap.set(col.header, col.header.length > 15 ? 175 : 150);
    });
  };

  const processModel = (model: ComparisonViewModel | undefined): IToolGridData => {
    const data: IToolGridData = {
      title: model?.compareTitle ?? '',
      headers: [],
      childHeaders: [],
      items: [],
      filterColumns: [],
    };

    //start with static headers
    data.headers.push({
      key: productSeriesColumnKey,
      modelIndex: -1,
      align: 'center',
      bindingValue: 'productName',
      headerText: t('Product'), // this will need to be passed as props eventually
      valueFormat: '',
      maxWidth: -1,
      minWidth: 165,
      visible: true,
      displayMode: CompareDisplayMode.AlwaysCantHide,
    });

    data.headers.push({
      key: configureColumnKey,
      modelIndex: -1,
      align: 'center',
      bindingValue: 'configUrl',
      headerText: t('Configure'),
      valueFormat: '',
      maxWidth: 300,
      minWidth: 100,
      visible: true,
      displayMode: CompareDisplayMode.AlwaysCantHide,
    });

    data.headers.push({
      key: reportColumnKey,
      modelIndex: -1,
      align: 'center',
      bindingValue: '',
      headerText: t('RPT'),
      valueFormat: '',
      maxWidth: -1,
      minWidth: 50,
      visible: false,
      displayMode: CompareDisplayMode.Hidden,
    });

    let containsNeverFilterColumn = false;
    let childDataAttributeIndex = -1;

    //append configured headers
    model?.attributeHeaders?.forEach((header, index) => {
      if (header.displayOnCompareScreen !== CompareDisplayMode.DataOnly) {
        const visible = header.displayOnCompareScreen === CompareDisplayMode.Always || header.displayOnCompareScreen === CompareDisplayMode.AlwaysCantHide;
        switch (header.originalKeyName) {
          case optimizationLogColumnKey: {
            data.headers.push({
              key: header.originalKeyName,
              modelIndex: index,
              align: 'center',
              bindingValue: '',
              headerText: header.shortName ?? '',
              valueFormat: '',
              maxWidth: -1,
              minWidth: 50,
              visible: visible,
              displayMode: header.displayOnCompareScreen as CompareDisplayMode,
            });
            break;
          }
          case childColumnKey: {
            childDataAttributeIndex = index;

            let dataString = '';
            if (model.products && model.products[0].attributes) {
              dataString = model.products[0].attributes[childDataAttributeIndex].valueString ?? '';
            }
            const childCompareData: ICompareChildData = JSON.parse(dataString);
            updateInnerFlexGridMinWidthMap(childCompareData.columns);
            childCompareData.columns.forEach((col, j) => {
              data.childHeaders.push({
                key: j.toString(),
                modelIndex: 0,
                align: 'center',
                bindingValue: 'values[' + j.toString() + ']',
                headerText: col.header,
                valueFormat: '',
                maxWidth: 0,
                minWidth: 0,
                visible: true,
                displayMode: CompareDisplayMode.Always,
              });
            });
            break;
          }
          default: {
            let format = '';
            let bindingString = 'valueString';
            let headerText = header.shortName ?? '';
            headerText += header.units ? ` ${header.units}` : '';
            switch (header.displayType) {
              case 'NumberPrec2': {
                format = '0.##';
                bindingString = 'valueNumber';
                break;
              }
              case 'NumberPrec3': {
                format = '0.###';
                bindingString = 'valueNumber';
                break;
              }
            }
            const binding = 'attributes[' + index + '].' + bindingString;
            if (
              props.productAttributeKeyForGrouping &&
              props.productAttributeKeyForGrouping.length > 0 &&
              header.originalKeyName === props.productAttributeKeyForGrouping
            ) {
              groupByBinding = binding;
            }

            data.headers.push({
              key: header.originalKeyName ?? '',
              modelIndex: index,
              align: 'center',
              bindingValue: binding,
              headerText: headerText,
              valueFormat: format,
              maxWidth: header.maxWidth ?? -1,
              minWidth: header.minWidth ?? 0,
              visible: visible,
              displayMode: header.displayOnCompareScreen as CompareDisplayMode,
            });
          }
        }
      }

      if (model.neverFilterColumns) {
        if (model.neverFilterColumns.findIndex((neverColumn) => neverColumn === header.shortName) >= 0) {
          containsNeverFilterColumn = true;
        } else {
          data.filterColumns.push(header.originalKeyName ?? '');
        }
      }
    });

    if (!containsNeverFilterColumn) {
      //we didn't find any column for which we want to disable filtering.. so show them all by setting the list empty.
      data.filterColumns = [];
    }

    model?.products?.forEach((product) => {
      const compareItem: IToolItem = {
        key: (product.index ?? 0).toString(),
        attributes: [],
        children: [],
        seriesName: product.title ?? '',
        productName: product.productName ?? '',
        configUrl: product.configUrl ?? '',
      };

      product.attributes?.forEach((attr) => {
        compareItem.attributes.push({
          valueNumber: attr.valueNumber ?? 0,
          valueString: attr.valueString ?? '',
          displayNumber: 0,
        });
      });

      if (childDataAttributeIndex >= 0) {
        //pull child grid data out of attribute
        let dataString = '';
        if (product.attributes) {
          dataString = product.attributes[childDataAttributeIndex].valueString ?? '';
        }
        const childCompareData: ICompareChildData = JSON.parse(dataString);
        childCompareData.itemsSource.forEach((item) => {
          const childItem: IToolChildItem = { values: [] };
          childCompareData.columns.forEach((col) => {
            childItem.values.push(item[col.binding]);
          });
          compareItem.children.push(childItem);
        });
      }
      data.items.push(compareItem);
    });

    return data;
  };

  const [gridData, setGridData] = React.useState<IToolGridData>(processModel(props.viewModel));
  const [gridPageSize, setGridPageSize] = useState(25);
  const [currentFilterDefinition, setCurrentFilterDefinition] = useState<string>();
  const [productView, setProductView] = useState<CollectionView>(
    new CollectionView(gridData.items, {
      pageSize: gridPageSize,
      sortDescriptions: [],
      groupDescriptions: [],
    }),
  );
  const [optimizationLog, setOptimizationLog] = useState<OptimizationLogData | undefined>(undefined);
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const getChildData = (key: string): IToolChildItem[] => {
    return gridData.items.filter((it) => it.key == key)[0].children;
  };

  const onPageSizeChanged = (event: SelectChangeEvent<string>) => {
    setGridPageSize(Number(event.target.value));
    setProductView(
      (current) =>
        new CollectionView(current.sourceCollection, {
          pageSize: Number(event.target.value),
          sortDescriptions: [...current.sortDescriptions],
          groupDescriptions: [...current.groupDescriptions],
        }),
    );
  };

  const flexGridRef = React.useRef(null);
  const gridDetailRef = React.useRef(null);
  const gridFilterRef = React.useRef(null);

  const getOptimizationLogJoinedLines = (item: wjGridXlsx.XlsxFormatItemEventArgs, header: IToolHeaderData) => {
    const optimizationLogString = gridData.items[item.row].attributes[header.modelIndex].valueString;
    const optimizationLogData = JSON.parse(optimizationLogString) as OptimizationLogData;
    return optimizationLogData.Lines.join('\n');
  };

  const headerKeyToFormatFunctionMap = new Map();
  headerKeyToFormatFunctionMap.set(optimizationLogColumnKey, getOptimizationLogJoinedLines);

  const getCustomExportContent = (item: wjGridXlsx.XlsxFormatItemEventArgs) => {
    let content: string | null = null;
    const header = gridData.headers[item.col];
    if (headerKeyToFormatFunctionMap.has(header.key)) {
      const columnSpecificFormatter = headerKeyToFormatFunctionMap.get(header.key);
      content = columnSpecificFormatter(item, header);
    }
    return content;
  };

  function handleExport() {
    const currentReference = flexGridRef.current as unknown as WjRef<PureFlexGrid>;
    if (currentReference) {
      const excludedColumnNames = [configureColumnKey, reportColumnKey];
      const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(currentReference.control, {
        includeColumnHeaders: true,
        formatItem: function (item: wjGridXlsx.XlsxFormatItemEventArgs) {
          if (item.cell.classList.contains('wj-header')) {
            return item;
          }
          const isNumber = gridData.headers[item.col].bindingValue.endsWith('Number');
          const content = item.cell.textContent ?? '';
          const customContent = getCustomExportContent(item);

          if (isNumber) {
            const numberValue = convertDecimalStringToNumber(content, props.locale);
            item.xlsxCell.value = isNaN(numberValue) ? '' : numberValue;
          } else if (customContent) {
            item.xlsxCell.value = customContent;
          } else {
            item.xlsxCell.value = content;
          }

          return item;
        },
        includeColumns: function (column) {
          return !excludedColumnNames.includes(column.name as string);
        },
      });
      book.sheets[0].name = t('CompareData');
      const timestamp = new Date().toISOString().substring(0, 19).replace(/-|T|:/g, '');
      const name = props.part?.name ?? props.partCategoryName;
      const xlsxFileName = `Compare ${name} (${timestamp}).xlsx`;
      book.saveAsync(xlsxFileName);
      //set the gridPageSize back to default
      setProductView(
        (current) =>
          new CollectionView(current.sourceCollection, {
            pageSize: gridPageSize,
            sortDescriptions: [...current.sortDescriptions],
            groupDescriptions: [...current.groupDescriptions],
          }),
      );
    }
  }

  const pageChangedHandler = () => {
    setTimeout(() => {
      const currentReference = flexGridRef.current as unknown as WjRef<PureFlexGrid>;
      if (!currentReference) {
        return;
      }
      currentReference.control.autoSizeColumns();
    }, 100);
  };

  useEffect(() => {
    const currentReference = gridFilterRef.current as unknown as WjRef<PureFlexGridFilter>;
    if (currentReference) {
      currentReference.control.filterDefinition = currentFilterDefinition ?? '';
    }

    if (productView.pageSize === 0) {
      //export flexgrid data without paging
      handleExport();
    }
    productView.pageChanged.addHandler(pageChangedHandler);
  }, [productView]);

  const onCleanup = (e: Event) => {
    const customEvent = e as CustomEvent;
    if (!customEvent || !customEvent.detail?.workflowId) return;
    const currentReference = gridDetailRef.current as unknown as WjRef<FlexGridDetailProvider>;
    if (customEvent.detail.workflowId === props.workflowId && currentReference) {
      currentReference.control.grid.rows.clear();
    }
  };
  useEffect(() => {
    window.addEventListener(compareCleanupEventType, onCleanup);
    return () => {
      window.removeEventListener(compareCleanupEventType, onCleanup);
    };
  }, []);

  const closeCompareTool = () => {
    const currentReference = gridDetailRef.current as unknown as WjRef<FlexGridDetailProvider>;
    if (currentReference) {
      currentReference.control.grid.rows.clear();
    }
    props.closeCompare();
  };

  const launchConfiguration = async (cellContext: ICellTemplateContext) => {
    SizingToolService.closeConfigureTab();
    const idx = parseInt(cellContext.item.key);
    const response = await SizingToolService.createSizingConfigure({
      workflowId: props.workflowId,
      compareIndex: idx,
      partCategoryName: props.partCategoryName,
    });

    let iframeUrl;
    if (response?.data.redirectToWorkflowRouter) {
      iframeUrl = `${process.env.REACT_APP_WORKFLOW_ROUTER_URL}${response.data.redirectUrl}`;
    } else {
      iframeUrl = `${process.env.REACT_APP_LEGACY_BASE_HOST}${response?.data.redirectUrl}`;
    }
    const sizingToolState = SizingToolService.getCurrentState();
    if (props.part) {
      SizingToolService.setSizingToolState(props.part, sizingToolState.showMiniSizing, sizingToolState.createPartSizingData, sizingToolState.sizingUnderway);
    }
    launchConfigureTool(props.part, iframeUrl, cellContext.item.seriesName);
  };

  const openOptimizationLog = (cellContext: ICellTemplateContext) => {
    const header = gridData.headers[cellContext.col.index];
    const optimizationLogData = JSON.parse(cellContext.item.attributes[header.modelIndex].valueString);
    if (optimizationLogData) {
      setOptimizationLog(optimizationLogData);
    }
  };

  const openReport = (cellContext: ICellTemplateContext) => {
    console.log(cellContext);
  };

  const onFilterChanged = () => {
    const currentReference = gridFilterRef.current as unknown as WjRef<PureFlexGridFilter>;
    if (currentReference) {
      setCurrentFilterDefinition(currentReference.control.filterDefinition);
    }
  };

  const toggleGroupBySeries = () => {
    const newKeys = productView.groupDescriptions.length > 0 ? [] : [groupByBinding];
    setProductView(
      (current) =>
        new CollectionView(current.sourceCollection, {
          pageSize: gridPageSize,
          sortDescriptions: [...current.sortDescriptions],
          groupDescriptions: newKeys,
        }),
    );
  };

  const formatTitleString = (title: string | undefined) => {
    if (!title) return '';

    const len = title.length;
    if (title[len - 1] === ':') {
      return title.slice(0, len - 1);
    }
    return title;
  };

  const renderCellTemplate = (header: IToolHeaderData) => {
    switch (header.key) {
      case configureColumnKey: {
        return (
          <FlexGridCellTemplate
            cellType="Cell"
            template={(cellContext: ICellTemplateContext) => {
              return (
                <div onClick={() => launchConfiguration(cellContext)} className={styles['configure-select-button']}>
                  <h5>{header.headerText}</h5>
                </div>
              );
            }}
          />
        );
      }
      case optimizationLogColumnKey:
        return (
          <FlexGridCellTemplate
            cellType="Cell"
            template={(cellContext: ICellTemplateContext) => {
              return (
                <div onClick={() => openOptimizationLog(cellContext)} className={styles['report-icon']}>
                  <FontAwesomeIcon icon="file" />
                </div>
              );
            }}
          />
        );
      case reportColumnKey: {
        return (
          <FlexGridCellTemplate
            cellType="Cell"
            template={(cellContext: ICellTemplateContext) => {
              return (
                <div onClick={() => openReport(cellContext)} className={styles['report-icon']}>
                  <FontAwesomeIcon icon="file" />
                </div>
              );
            }}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  const renderGrid = () => {
    const configurableColumnElements = gridData.headers.map((header) => {
      return (
        <FlexGridColumn
          key={header.key}
          name={header.key}
          header={header.headerText}
          visible={header.visible}
          minWidth={header.minWidth}
          maxWidth={header.maxWidth <= 0 ? null : header.maxWidth}
          binding={header.bindingValue}
          format={header.valueFormat}
          align={header.align}
        >
          {renderCellTemplate(header)}
        </FlexGridColumn>
      );
    });

    const childColumnElements = gridData.childHeaders.map((header, index) => {
      return (
        <FlexGridColumn
          key={index.toString()}
          header={header.headerText}
          visible={header.visible}
          binding={'values[' + index.toString() + ']'}
          minWidth={innerFlexGridMinWidthMap.get(header.headerText)}
        />
      );
    });

    return (
      <FlexGrid
        ref={flexGridRef}
        autoGenerateColumns={false}
        initialized={(grid: PureFlexGrid) => {
          setTimeout(() => {
            grid.autoSizeColumns();
          }, 100);
        }}
        itemsSource={productView}
        selectionMode="Row"
        isReadOnly={true}
      >
        <FlexGridFilter
          ref={gridFilterRef}
          filterColumns={gridData.filterColumns} //defines which columns can have a filter
          filterDefinition={currentFilterDefinition} //holds the state of filter selections
          filterChanged={onFilterChanged}
        />
        {configurableColumnElements}
        <FlexGridDetail
          ref={gridDetailRef}
          template={(ctx: { item: { key: string } }) => (
            <FlexGrid
              autoGenerateColumns={false}
              initialized={(grid: PureFlexGrid) => {
                setTimeout(() => {
                  grid.autoSizeColumns();
                }, 100);
              }}
              selectionMode="Row"
              isReadOnly
              itemsSource={getChildData(ctx.item.key)}
            >
              {childColumnElements}
            </FlexGrid>
          )}
        />
      </FlexGrid>
    );
  };

  const handleSelectColumnChange = (e: React.ChangeEvent<HTMLInputElement>, targetHeaderKey: string, gridType: GridType) => {
    const gridDataUpdated = { ...gridData };
    const updatedHeaders = gridType === GridType.Main ? gridDataUpdated.headers : gridDataUpdated.childHeaders;
    const updatedHeader = updatedHeaders.find((header) => header.key === targetHeaderKey);
    if (updatedHeader) {
      updatedHeader.visible = e.target.checked;
      setGridData(gridDataUpdated);
    }
  };

  const renderGridColumnHeaders = (gridType: GridType) => {
    const excludedColumnNames = [productSeriesColumnKey, configureColumnKey, reportColumnKey];
    const mainHeaders = gridData.headers
      .filter((header) => !excludedColumnNames.includes(header.key))
      .filter((header) => header.displayMode === CompareDisplayMode.Always || header.displayMode === CompareDisplayMode.Optional);
    return (gridType === GridType.Main ? mainHeaders : gridData.childHeaders).map((header) => (
      <FormControlLabel
        className={styles['column-header-label']}
        key={header.key}
        control={<Checkbox onChange={(e) => handleSelectColumnChange(e, header.key, gridType)} checked={header.visible} />}
        label={header.headerText}
      />
    ));
  };

  const renderColumnPickerAccordions = () => {
    const accordionIds = [t('ManifoldColumns'), t('SegmentColumns')];
    return accordionIds.map((id) => (
      <Accordion key={id} defaultExpanded={id === t('ManifoldColumns')}>
        <AccordionSummary className={styles['accordion-header']} expandIcon={<FontAwesomeIcon className={styles['chevron-icon']} icon="chevron-down" />}>
          <Typography>{id}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles['main-grid-columns']}>
            <FormGroup>{renderGridColumnHeaders(id === t('ManifoldColumns') ? GridType.Main : GridType.Nested)}</FormGroup>
          </div>
        </AccordionDetails>
      </Accordion>
    ));
  };

  return (
    <div className={styles['compare-tool-container']}>
      <Dialog
        hideBackdrop
        open={!!optimizationLog}
        PaperProps={{
          style: {
            minHeight: '35%',
            maxHeight: '55%',
            minWidth: '45%',
            maxWidth: '85',
          },
        }}
        onClose={() => {
          setOptimizationLog(undefined);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle className={styles['optimization-log-dialog-header']} id="alert-dialog-title">
          {t(`${formatTitleString(optimizationLog?.Header)}`)}
          <div>
            <FontAwesomeIcon icon="xmark" onClick={() => setOptimizationLog(undefined)} />
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <ul>
            {optimizationLog?.Lines.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button
            className={styles['optimization-log-dialog-button']}
            onClick={() => {
              setOptimizationLog(undefined);
            }}
          >
            {t('OK')}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={showColumnPicker} onClose={() => setShowColumnPicker(false)} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <div className={styles['column-picker-wrapper']}>
          <div className={styles['column-picker-container']}>{renderColumnPickerAccordions()}</div>
          <div className={styles['column-picker-buttons-div']}>
            <Button className={styles['column-picker-dialog-button']} onClick={() => setShowColumnPicker(false)}>
              {t('OK')}
            </Button>
          </div>
        </div>
      </Modal>

      <div className={styles['compare-tool-header']}>
        <h2>{gridData?.title}</h2>
      </div>
      <div className={styles['compare-tool-buttons']}>
        <div className={styles['compare-tool-button-container']}>
          <div className={styles['compare-tool-back-to-sizing']} onClick={() => closeCompareTool()}>
            <FontAwesomeIcon icon="circle-left" size="lg" />
            <Link component="button" variant="body1" className={styles['back-to-sizing-link']} underline="hover">
              {t('BackToSizing')}
            </Link>
          </div>
          <Button id="pickerButtonDiv" onClick={() => setShowColumnPicker(true)} className={styles['compare-tool-button']}>
            {t('SelectColumns')}
          </Button>
          <Button id="groupBySeriesButton" onClick={() => toggleGroupBySeries()} className={styles['compare-tool-button']}>
            {productView.groupDescriptions.length > 0 ? t('RemoveGrouping') : t('GroupBySeries')}
          </Button>
          <Button
            className={styles['compare-tool-button']}
            onClick={() => {
              //disable paging before export
              setProductView(
                (current) =>
                  new CollectionView(current.sourceCollection, {
                    pageSize: 0,
                    sortDescriptions: [...current.sortDescriptions],
                    groupDescriptions: [...current.groupDescriptions],
                  }),
              );
            }}
          >
            {t('Export')}
          </Button>
        </div>
      </div>
      <div className={styles['compare-tool-grid']}>{renderGrid()}</div>
      <div className={styles['compare-tool-footer']}>
        <div className={styles['compare-tool-footer-container']}>
          <CollectionViewNavigator
            headerFormat={t('CollectionViewNavigatorHeaderFormat')}
            byPage={true}
            cv={productView}
            className={styles['collection-view-navigator']}
          />
          <div className={styles['page-size-label']}>
            <span>{t('PageSize')}:</span>
          </div>
          <div className={styles['page-size-controls']}>
            <FormControl className={styles['modal-input-field']} size="small">
              <Select onChange={onPageSizeChanged} defaultValue="25" color="secondary">
                <MenuItem value="10">10</MenuItem>
                <MenuItem value="25">25</MenuItem>
                <MenuItem value="50">50</MenuItem>
                <MenuItem value="100">100</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>
    </div>
  );
}
